import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { Artwork, Collection, GalleryState } from "../types";
import { SEED } from "../data/seed";
import { newId } from "../utils/image";
import { apiMode, apiUrl } from "../utils/api";

const cloneSeed = () => JSON.parse(JSON.stringify(SEED)) as GalleryState;

const STORE_KEY = "tlart.gallery.v1";
const TOKEN_KEY = "tlart.token.v1";

/** Injected by esbuild at build time. Empty string means localStorage mode. */
function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error ?? `HTTP ${res.status}`), {
      status: res.status,
    });
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

interface GalleryContextValue extends GalleryState {
  loading: boolean;
  addArtwork: (a: Omit<Artwork, "id" | "createdAt">) => Artwork;
  updateArtwork: (id: string, patch: Partial<Artwork>) => void;
  removeArtwork: (id: string) => void;
  addCollection: (c: Omit<Collection, "id" | "createdAt">) => Collection;
  updateCollection: (id: string, patch: Partial<Collection>) => void;
  removeCollection: (id: string) => void;
  resetToSeed: () => void;
}

const GalleryContext = createContext<GalleryContextValue | null>(null);

function loadLocal(): GalleryState {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as GalleryState;
      if (Array.isArray(parsed.artworks) && Array.isArray(parsed.collections))
        return parsed;
    }
  } catch {
    /* fall through to seed */
  }
  return cloneSeed();
}

export function GalleryProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GalleryState>(() =>
    apiMode ? { artworks: [], collections: [] } : loadLocal(),
  );
  const [loading, setLoading] = useState(apiMode);

  // API mode: fetch initial data from server.
  useEffect(() => {
    if (!apiMode) return;
    Promise.all([
      apiFetch<Artwork[]>("/api/artworks"),
      apiFetch<Collection[]>("/api/collections"),
    ])
      .then(([artworks, collections]) => {
        setState({ artworks, collections });
      })
      .catch((err) => {
        console.warn(
          "TLArt: API unavailable, falling back to localStorage.",
          err,
        );
        setState(loadLocal());
      })
      .finally(() => setLoading(false));
  }, []);

  // localStorage mode: persist state on every change.
  useEffect(() => {
    if (apiMode) return;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (err) {
      console.warn("TLArt: could not save gallery (storage full?).", err);
    }
  }, [state]);

  /** POST to API and replace the optimistic entry with the server's version (real ID). */
  function syncCreate(
    endpoint: string,
    body:
      | Omit<Artwork, "id" | "createdAt">
      | Omit<Collection, "id" | "createdAt">,
    tempId: string,
    listKey: "artworks" | "collections",
  ) {
    apiFetch<Artwork | Collection>(endpoint, {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((saved) => {
        setState((s) => ({
          ...s,
          [listKey]: (s[listKey] as Array<{ id: string }>).map((item) =>
            item.id === tempId ? saved : item,
          ),
        }));
      })
      .catch((err) => {
        console.error(`TLArt: failed to save to API (${endpoint}):`, err);
        // Roll back the optimistic entry.
        setState((s) => ({
          ...s,
          [listKey]: (s[listKey] as Array<{ id: string }>).filter(
            (item) => item.id !== tempId,
          ),
        }));
      });
  }

  const addArtwork = useCallback((a: Omit<Artwork, "id" | "createdAt">) => {
    const art: Artwork = { ...a, id: newId(), createdAt: Date.now() };
    setState((s) => ({ ...s, artworks: [art, ...s.artworks] }));
    if (apiMode) syncCreate("/api/artworks", a, art.id, "artworks");
    return art;
  }, []);

  const updateArtwork = useCallback((id: string, patch: Partial<Artwork>) => {
    setState((s) => ({
      ...s,
      artworks: s.artworks.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    }));
    if (apiMode) {
      apiFetch(`/api/artworks/${id}`, {
        method: "PUT",
        body: JSON.stringify(patch),
      }).catch((err) => console.error("TLArt: update artwork failed:", err));
    }
  }, []);

  const removeArtwork = useCallback((id: string) => {
    setState((s) => ({
      ...s,
      artworks: s.artworks.filter((a) => a.id !== id),
      collections: s.collections.map((c) =>
        c.coverArtId === id ? { ...c, coverArtId: null } : c,
      ),
    }));
    if (apiMode) {
      apiFetch(`/api/artworks/${id}`, { method: "DELETE" }).catch((err) =>
        console.error("TLArt: delete artwork failed:", err),
      );
    }
  }, []);

  const addCollection = useCallback(
    (c: Omit<Collection, "id" | "createdAt">) => {
      const col: Collection = { ...c, id: newId(), createdAt: Date.now() };
      setState((s) => ({ ...s, collections: [...s.collections, col] }));
      if (apiMode)
        if (apiMode) syncCreate("/api/collections", c, col.id, "collections");
      return col;
    },
    [],
  );

  const updateCollection = useCallback(
    (id: string, patch: Partial<Collection>) => {
      setState((s) => ({
        ...s,
        collections: s.collections.map((c) =>
          c.id === id ? { ...c, ...patch } : c,
        ),
      }));
      if (apiMode) {
        apiFetch(`/api/collections/${id}`, {
          method: "PUT",
          body: JSON.stringify(patch),
        }).catch((err) =>
          console.error("TLArt: update collection failed:", err),
        );
      }
    },
    [],
  );

  const removeCollection = useCallback((id: string) => {
    setState((s) => ({
      ...s,
      collections: s.collections.filter((c) => c.id !== id),
      artworks: s.artworks.map((a) =>
        a.collectionId === id ? { ...a, collectionId: null } : a,
      ),
    }));
    if (apiMode) {
      apiFetch(`/api/collections/${id}`, { method: "DELETE" }).catch((err) =>
        console.error("TLArt: delete collection failed:", err),
      );
    }
  }, []);

  const resetToSeed = useCallback(() => setState(cloneSeed()), []);

  const value = useMemo(
    () => ({
      ...state,
      loading,
      addArtwork,
      updateArtwork,
      removeArtwork,
      addCollection,
      updateCollection,
      removeCollection,
      resetToSeed,
    }),
    [
      state,
      loading,
      addArtwork,
      updateArtwork,
      removeArtwork,
      addCollection,
      updateCollection,
      removeCollection,
      resetToSeed,
    ],
  );

  return (
    <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>
  );
}

export function useGallery(): GalleryContextValue {
  const ctx = useContext(GalleryContext);
  if (!ctx) throw new Error("useGallery must be used within GalleryProvider");
  return ctx;
}
